// API client for Kalibr backend
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://kalibr-backend.fly.dev';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get Clerk token
const getClerkToken = async () => {
  try {
    // Access Clerk instance from window (available after ClerkProvider mounts)
    if (window.Clerk?.session?.getToken) {
      const token = await window.Clerk.session.getToken({
        skipCache: true  // Force fresh token with current context
      });
      return token;
    }
    return null;
  } catch (error) {
    console.error('Failed to get Clerk token:', error);
    return null;
  }
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  async (config) => {
    const token = await getClerkToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      // Clerk will handle the sign-out automatically via afterSignOutUrl
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const apiClient = {
  // Fetch traces
  getTraces: async (params = {}) => {
    const { environment, endpoint, hours = 24, limit = 100, start_date, end_date } = params;
    const queryParams = new URLSearchParams();
    if (environment) queryParams.append('environment', environment);
    if (endpoint) queryParams.append('endpoint', endpoint);
    // Use date range if provided, otherwise fall back to hours
    if (start_date && end_date) {
      queryParams.append('start_date', start_date);
      queryParams.append('end_date', end_date);
    } else {
      queryParams.append('hours', hours);
    }
    queryParams.append('limit', limit);

    const response = await api.get(`/api/traces?${queryParams.toString()}`);
    return response.data;
  },

  // Fetch single trace detail
  getTraceDetail: async (traceId) => {
    const response = await api.get(`/api/traces/${traceId}`);
    return response.data;
  },

  // Fetch cost summary
  getCostSummary: async (params = {}) => {
    const { environment, hours = 24, start_date, end_date } = params;
    const queryParams = new URLSearchParams();
    if (environment) queryParams.append('environment', environment);
    // Use date range if provided, otherwise fall back to hours
    if (start_date && end_date) {
      queryParams.append('start_date', start_date);
      queryParams.append('end_date', end_date);
    } else {
      queryParams.append('hours', hours);
    }

    const response = await api.get(`/api/costs/summary?${queryParams.toString()}`);
    return response.data;
  },

  // Fetch modality summary
  getModalitySummary: async (params = {}) => {
    const { hours = 24, start_date, end_date } = params;
    const queryParams = new URLSearchParams();
    if (start_date && end_date) {
      queryParams.append('start_date', start_date);
      queryParams.append('end_date', end_date);
    } else {
      queryParams.append('hours', hours);
    }

    const response = await api.get(`/api/modalities/summary?${queryParams.toString()}`);
    return response.data;
  },

  // Fetch alerts
  getAlerts: async (params = {}) => {
    const { hours = 168 } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('hours', hours);

    const response = await api.get(`/api/alerts?${queryParams.toString()}`);
    return response.data;
  },

  // Health check
  getHealth: async () => {
    const response = await api.get('/api/health');
    return response.data;
  },

  // Billing endpoints
  getBillingUsage: async () => {
    const response = await api.get('/api/billing/usage');
    return response.data;
  },

  getBillingUsageHistory: async (months = 12) => {
    const response = await api.get(`/api/billing/usage/history?months=${months}`);
    return response.data;
  },

  getBillingTiers: async () => {
    const response = await api.get('/api/billing/tiers');
    return response.data;
  },

  getStripeStatus: async () => {
    const response = await api.get('/api/billing/stripe/status');
    return response.data;
  },

  createCheckoutSession: async (targetTier, successUrl, cancelUrl) => {
    const response = await api.post('/api/billing/checkout', {
      target_tier: targetTier,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  },

  createPortalSession: async (returnUrl) => {
    const response = await api.post('/api/billing/portal', {
      return_url: returnUrl,
    });
    return response.data;
  },

  linkStripeCustomer: async () => {
    const response = await api.post('/api/billing/link-stripe-customer');
    return response.data;
  },

  // Credits balance (usage-based billing)
  getCreditsBalance: async () => {
    const response = await api.get('/api/billing/credits');
    return response.data;
  },

  createCreditsCheckout: async (amountUsd, successUrl, cancelUrl) => {
    const response = await api.post('/api/billing/credits/checkout', {
      amount_usd: amountUsd,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  },

  // Intelligence endpoints
  getIntelligenceHealth: async () => {
    const response = await api.get('/api/intelligence/health');
    return response.data;
  },

  getIntelligencePolicy: async (goal, optimizeFor = 'balanced') => {
    const response = await api.post('/api/intelligence/policy', {
      goal,
      optimize_for: optimizeFor,
    });
    return response.data;
  },

  getIntelligencePatterns: async (taskType) => {
    const response = await api.get(`/api/intelligence/patterns?task_type=${encodeURIComponent(taskType)}`);
    return response.data;
  },

  getDistinctGoals: async () => {
    const response = await api.get('/api/intelligence/goals');
    return response.data;
  },

  // Validation errors (for PR3 compatibility)
  getValidationErrors: async (traceId) => {
    try {
      const response = await api.get(`/api/v1/validation/errors/${traceId}`);
      return response.data;
    } catch (error) {
      // Endpoint may not exist yet (before PR3), return empty
      if (error.response?.status === 404) {
        return { errors: [] };
      }
      throw error;
    }
  },

  // Routing endpoints
  getRoutingPaths: async (goal) => {
    const queryParams = new URLSearchParams();
    if (goal) queryParams.append('goal', goal);
    const queryString = queryParams.toString();
    const url = queryString ? `/api/routing/paths?${queryString}` : '/api/routing/paths';
    const response = await api.get(url);
    return response.data;
  },

  registerPath: async (data) => {
    const response = await api.post('/api/routing/paths', data);
    return response.data;
  },

  disablePath: async (pathId) => {
    const response = await api.delete(`/api/routing/paths/${pathId}`);
    return response.data;
  },

  getRoutingDecision: async (goal, taskRiskLevel) => {
    const response = await api.post('/api/routing/decide', {
      goal,
      task_risk_level: taskRiskLevel,
    });
    return response.data;
  },

  getExplorationConfig: async (goal) => {
    const queryParams = new URLSearchParams();
    if (goal) queryParams.append('goal', goal);
    const queryString = queryParams.toString();
    const url = queryString ? `/api/routing/config?${queryString}` : '/api/routing/config';
    const response = await api.get(url);
    return response.data;
  },

  setExplorationConfig: async (data) => {
    const response = await api.post('/api/routing/config', data);
    return response.data;
  },

  getGoalStats: async (goal) => {
    const response = await api.get(`/api/routing/stats?goal=${encodeURIComponent(goal)}`);
    return response.data;
  },

  getRecentOutcomes: async (goal, limit = 20) => {
    const response = await api.get(`/api/routing/outcomes?goal=${encodeURIComponent(goal)}&limit=${limit}`);
    return response.data;
  },

  // Get executions filtered by goal
  getExecutionsByGoal: async (goal, hours = 168, limit = 50) => {
    const response = await api.get(`/api/executions/by-goal/${encodeURIComponent(goal)}?hours=${hours}&limit=${limit}`);
    return response.data;
  },

  // Get execution tree (spans) for a specific trace
  getExecutionTree: async (traceId) => {
    const response = await api.get(`/api/executions/${traceId}`);
    // Backend wraps the span tree: { execution: <root_span>, trace_id, tenant_id }
    // SpanTree component expects the root span directly, not the envelope
    return response.data?.execution ?? response.data;
  },

  // API Keys endpoints
  getApiKeys: async () => {
    const response = await api.get('/api/settings/api-keys');
    return response.data;
  },

  createApiKey: async (data) => {
    const response = await api.post('/api/settings/api-keys', data);
    return response.data;
  },

  deleteApiKey: async (keyId) => {
    const response = await api.delete(`/api/settings/api-keys/${keyId}`);
    return response.data;
  },

  // Provisioning tokens
  getProvisioningTokens: async () => {
    const response = await api.get('/api/provisioning/tokens');
    return response.data;
  },
  createProvisioningToken: async (data) => {
    const response = await api.post('/api/provisioning/tokens', data);
    return response.data;
  },
  revokeProvisioningToken: async (tokenId) => {
    const response = await api.delete(`/api/provisioning/tokens/${tokenId}`);
    return response.data;
  },
  getSubKeys: async () => {
    const response = await api.get('/api/provisioning/sub-keys');
    return response.data;
  },
  revokeSubKey: async (keyId) => {
    const response = await api.delete(`/api/provisioning/sub-keys/${keyId}`);
    return response.data;
  },

  // Self-serve credentials — returns api_key + tenant_id, auto-creates key on first call
  getCredentials: async () => {
    const response = await api.get('/api/me/credentials');
    return response.data;
  },

  // Analytics summary — cost history, traffic breakdown, success rate
  getAnalyticsSummary: async (months = 6) => {
    const response = await api.get('/api/analytics/summary', { params: { months } });
    return response.data;
  },

  getAnalyticsSavings: async () => {
    const response = await api.get('/api/analytics/savings-estimate');
    return response.data;
  },

  getAnalyticsByGoal: async () => {
    const response = await api.get('/api/analytics/by-goal');
    return response.data;
  },

  getGate3Retrospective: async (days = 14) => {
    const response = await api.get('/api/analytics/gate3-retrospective', { params: { days } });
    return response.data;
  },

  // CLI Auth (device linking)

  // Pipeline runs — heal telemetry and targeted repair
  getPipelineRuns: async (limit = 50, goal = null, hours = 168) => {
    const params = { limit, hours };
    if (goal) params.goal = goal;
    const response = await api.get('/api/pipeline-runs', { params });
    return response.data;
  },
  repairPipeline: async (body) => {
    const response = await api.post('/api/pipeline-runs/repair', body);
    return response.data;
  },

  // Agent feedback (behavioral signals per goal)
  getAgentFeedback: async (days = 30) => {
    const response = await api.get(`/api/signals/agent-feedback?days=${days}`);
    return response.data;
  },

  // Agent heals by goal (execution tab)
  getAgentHeals: async (goal, days = 30) => {
    const params = { days };
    if (goal) params.goal = goal;
    const response = await api.get('/api/analytics/by-goal', { params });
    return response.data;
  },

  // Savings estimate (cost tab)
  getSavingsEstimate: async () => {
    const response = await api.get('/api/analytics/savings-estimate');
    return response.data;
  },

  // Signals summary
  getSignalsSummary: async (windowHours = 168) => {
    const response = await api.get(`/api/intelligence/signals/summary?window_hours=${windowHours}`);
    return response.data;
  },

  approveCliAuth: async (userCode) => {
    const response = await api.post('/api/cli-auth/approve', { user_code: userCode });
    return response.data;
  },

  // Provider Keys (encrypted vault for run_pipeline execution)
  getProviderKeys: async () => {
    const response = await api.get('/api/settings/provider-keys');
    return response.data;
  },
  getSupportedProviders: async () => {
    const response = await api.get('/api/settings/provider-keys/supported');
    return response.data;
  },
  setProviderKey: async (provider, apiKey) => {
    const response = await api.put(`/api/settings/provider-keys/${provider}`, { provider, api_key: apiKey });
    return response.data;
  },
  deleteProviderKey: async (provider) => {
    const response = await api.delete(`/api/settings/provider-keys/${provider}`);
    return response.data;
  },
};

export default api;
